import type { AxiosResponse } from 'axios';
import type { RequestOptions, Result } from '../types/axios';
import type { CreateAxiosOptions } from '../define/axios/axiosTransform';
import { VAxios } from '../define/axios/Axios';
import { isString } from '../utils/index';
import { joinTimestamp, formatRequestDate } from '../define/axios/helper';
import { AxiosRetry } from '../define/axios/axiosRetry';
import axios from 'axios';
import { RequestEnum, ContentTypeEnum } from '../enums/index';
import { merge } from 'lodash-es';

import { createApis } from '../configs/index';

/**
 * @description: 数据处理，方便区分多种处理方式
 */
const generateTransform = (
  getToken: Function,
  responseCb: Function,
  responseCatchCb: Function,
) => ({
  /**
   * @description: 处理响应数据。如果数据不是预期格式，可直接抛出错误
   */
  transformResponseHook: (res: AxiosResponse<Result>, options: RequestOptions) => {
    // 错误的时候返回
    const { data } = res;
    if (!data) {
      // return '[HTTP] Request has no return value';
      throw new Error('response is null');
    }

    responseCb?.(data);
    //  这里 code，result，message为 后台统一的字段，需要在 types.ts内修改为项目自己的接口返回格式
    const { result } = data;

    if (options.getFullResponse) {
      return res;
    }

    return result;
  },

  // 请求之前处理config
  beforeRequestHook: (config, options) => {
    const { apiUrl, joinTime = true } = options;
    const formatFun = options?.formatRequestDate || formatRequestDate;

    if (apiUrl && isString(apiUrl)) {
      config.url = `${apiUrl}${config.url}`;
    }
    const params = config.params || {};
    const data = config.data || false;
    data && !isString(data) && formatFun(data);
    if (config.method?.toUpperCase() === RequestEnum.GET) {
      if (!isString(params)) {
        // 给 get 请求加上时间戳参数，避免从缓存中拿数据。
        config.params = Object.assign(params || {}, joinTimestamp(joinTime, false));
      } else {
        // 兼容restful风格
        config.url = config.url + params + `${joinTimestamp(joinTime, true)}`;
        config.params = undefined;
      }
    } else {
      if (!isString(params)) {
        formatFun(params);
        if (
          Reflect.has(config, 'data') &&
          config.data &&
          (Object.keys(config.data).length > 0 || config.data instanceof FormData)
        ) {
          config.data = data;
          config.params = params;
        } else {
          // 非GET请求如果没有提供data，则将params视为data
          config.data = params;
          config.params = undefined;
        }
      } else {
        // 兼容restful风格
        config.url = config.url + params;
        config.params = undefined;
      }
    }
    return config;
  },

  /**
   * @description: 请求拦截器处理
   */
  requestInterceptors: async (config, options) => {
    // 请求之前处理config

    const token =
      'Authorization' in config.headers ? config.headers.Authorization : await getToken();

    if (token && (config as Recordable)?.requestOptions?.withToken !== false) {
      // jwt token
      (config as Recordable).headers.Authorization = options.authenticationScheme
        ? `${options.authenticationScheme} ${token}`
        : token;
    }
    return config;
  },

  /**
   * @description: 响应拦截器处理
   */
  responseInterceptors: (res: AxiosResponse<any>) => {
    return res;
  },

  /**
   * @description: 响应错误处理
   */
  responseCatchHook: (axiosInstance: AxiosResponse, error: any) => {
    const { code, message, config } = error || {};
    const err: string = error?.toString?.() ?? '';
    let errMessage = message || '';

    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    responseCatchCb?.(error);

    try {
      if (code === 'ECONNABORTED' && message.indexOf('timeout') !== -1) {
        errMessage = 'timeout';
      }
      if (err?.includes('Network Error')) {
        errMessage = 'Network Error';
      }

      if (errMessage) {
        return Promise.reject(error);
      }
    } catch (error) {
      throw new Error(error as unknown as string);
    }

    // 添加自动重试机制 保险起见 只针对GET请求
    const retryRequest = new AxiosRetry();
    const { isRetry } = config.requestOptions;
    config.method?.toUpperCase() === RequestEnum.GET &&
      isRetry &&
      // @ts-ignore
      retryRequest.retry(axiosInstance, error);
    return Promise.reject(error);
  },
});

function createAxios(
  opt: Partial<CreateAxiosOptions>,
  getToken: () => Promise<string | null>,
  responseCb: (data) => void,
  responseCatchCb: (err) => void,
) {
  return new VAxios(
    merge(
      {
        // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Authentication#authentication_schemes
        // authentication schemes，e.g: Bearer
        // authenticationScheme: 'Bearer',
        authenticationScheme: '',
        timeout: 60 * 1000,
        // 基础接口地址
        // baseURL: globSetting.apiUrl,

        headers: { 'Content-Type': ContentTypeEnum.JSON },
        // 如果是form-data格式
        // headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
        // 数据处理方式
        transform: generateTransform(getToken, responseCb, responseCatchCb),
        // 配置项，下面的选项都可以在独立的接口请求中覆盖
        requestOptions: {
          preventErrorHandle: false,
          getFullResponse: false,
          // 格式化提交参数时间
          formatDate: true,
          // 接口地址
          apiUrl: '/api',
          //  是否加入时间戳
          joinTime: true,
          // 忽略重复请求
          ignoreCancelToken: true,
          // 是否携带token
          withToken: true,
          // 重试机制，默认不开启
          isRetry: false,
          retryRequest: {
            count: 5,
            waitTime: 100,
          },
        },
      },
      opt || {},
    ),
  );
}

export type ApiContainer = {
  [K in keyof typeof createApis]: ReturnType<typeof createApis[K]>;
};

// @ts-ignore api 缓存容器
const apiContainer: ApiContainer = {};

// 生成 API
export function generateApis(
  configs: {
    defaultAxiosOptions?: CreateAxiosOptions;
    getToken: () => Promise<string | null>;
    responseCb: (data) => void;
    responseCatchCb: (err) => void;
  },
  apis = createApis,
) {
  const { defaultAxiosOptions = {}, getToken, responseCb, responseCatchCb } = configs;
  const defHttp = createAxios(defaultAxiosOptions, getToken, responseCb, responseCatchCb);
  Object.keys(apis).forEach((key) => {
    if (!apiContainer[key]) {
      // @ts-ignore
      apiContainer[key] = apis[key](defHttp);
    }
  });

  return { apiContainer, defHttp };
}
